import { recordAgentAlbertAlertSeenApi } from "./api";

const storageKey = (locationId) =>
  `agentAlbertAlertSeen:${String(locationId || "").trim()}`;

export const getStableAgentAlbertAlertId = (alert) =>
  String(alert?.id || alert?._id || "").trim();

export const getSeenAgentAlbertAlertIds = (locationId) => {
  if (!locationId) return new Set();
  try {
    const raw = localStorage.getItem(storageKey(locationId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.map(String));
  } catch {
    return new Set();
  }
};

const getCurrentUserSnapshot = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const getCurrentUserId = () => {
  const user = getCurrentUserSnapshot();
  return String(user?.id || user?._id || "").trim();
};

const upsertLocalSeenBy = (alert, user) => {
  const userId = String(user?.id || user?._id || "").trim();
  const userName = String(user?.name || "").trim() || "User";
  const userEmail = String(user?.email || "").trim();
  if (!userId) return alert;

  const existing = Array.isArray(alert?.seenBy) ? alert.seenBy : [];
  const filtered = existing.filter((row) => String(row?.userId) !== userId);
  return {
    ...alert,
    seenBy: [
      {
        userId,
        userName,
        userEmail,
        seenAt: new Date().toISOString(),
      },
      ...filtered,
    ],
  };
};

export const markAgentAlbertAlertSeen = (locationId, alert, userOverride) => {
  const id = getStableAgentAlbertAlertId(alert);
  if (!locationId || !id) return alert;

  const set = getSeenAgentAlbertAlertIds(locationId);
  const wasSeenAlready = set.has(id);
  if (!wasSeenAlready) {
    set.add(id);
    try {
      localStorage.setItem(storageKey(locationId), JSON.stringify([...set]));
    } catch {
      /* ignore quota errors */
    }
  }

  const user = userOverride || getCurrentUserSnapshot();
  if (user && !wasSeenAlready) {
    recordAgentAlbertAlertSeenApi({
      locationId,
      alertId: id,
    }).catch((err) => {
      console.warn("[Shift Report] Failed to save seen-by on server", err);
    });
  }

  window.dispatchEvent(
    new CustomEvent("agentAlbertAlertSeenUpdated", {
      detail: { locationId: String(locationId), alertId: id },
    }),
  );

  return user && !wasSeenAlready ? upsertLocalSeenBy(alert, user) : alert;
};

export const countUnreadAgentAlbertAlerts = (alerts, locationId) => {
  if (!locationId || !Array.isArray(alerts)) return 0;
  const seen = getSeenAgentAlbertAlertIds(locationId);
  const currentUserId = getCurrentUserId();
  return alerts.filter((a) => {
    const id = getStableAgentAlbertAlertId(a);
    if (!id || seen.has(id)) return false;
    if (!currentUserId) return true;
    const seenByCurrentUser = Array.isArray(a?.seenBy)
      ? a.seenBy.some(
          (row) => String(row?.userId || "").trim() === currentUserId,
        )
      : false;
    return !seenByCurrentUser;
  }).length;
};

export const formatAgentAlbertAlertSeenBy = (seenBy) => {
  if (!Array.isArray(seenBy) || !seenBy.length) return "—";
  const names = seenBy
    .map((row) => String(row?.userName || row?.userEmail || "").trim())
    .filter(Boolean);
  if (!names.length) return "—";
  return names.join(", ");
};

export const formatAgentAlbertAlertSeenByTitle = (seenBy) => {
  if (!Array.isArray(seenBy) || !seenBy.length) return "";
  return seenBy
    .map((row) => {
      const name = String(row?.userName || row?.userEmail || "User").trim();
      const at = row?.seenAt ? new Date(row.seenAt).toLocaleString() : "";
      return at ? `${name} — ${at}` : name;
    })
    .join("\n");
};
